import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import {
  TCreateLocationsPayload,
  TCreateLocationsResponse,
} from '~/features/agents/models'
import { queryKeys } from '~/features/agents/utils'
import { EHttpService, EServerPath, TErrorResponse } from '~/models/api'
import HttpService from '~/services/http'
import { HttpServiceBaseResponse } from '~/services/http/models'
import { store } from '~/store'

type TCreateLocationsVariables = {
  headers?: Record<string, string>
  hideToast?: boolean
  orgId?: string
  payload: TCreateLocationsPayload
}

const createLocations = async ({
  headers,
  orgId,
  payload,
}: {
  headers?: Record<string, string>
  orgId: string
  payload: TCreateLocationsPayload
}) =>
  HttpService.post<TCreateLocationsResponse>(
    {
      headers,
      path: EServerPath.locations,
      query: { orgId },
      payload,
    },
    EHttpService.agent,
  )

function useCreateLocations() {
  const queryClient = useQueryClient()
  const storeOrgId = store((state) => state.user.organizationId) || ''

  return useMutation<
    HttpServiceBaseResponse<TCreateLocationsResponse>,
    TErrorResponse,
    TCreateLocationsVariables
  >({
    mutationFn: ({ headers, orgId, payload }) =>
      createLocations({
        headers,
        orgId: orgId || storeOrgId,
        payload,
      }),
    onSuccess: (_response, { orgId }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.locations(orgId || storeOrgId),
      })
    },
    onError: (error, { hideToast }) => {
      if (hideToast) {
        return
      }

      const message =
        error?.response?.data?.message ||
        'Could not create locations. Please try again.'

      toast.error(message)
    },
  })
}

export default useCreateLocations
